// frontend/src/Sidebar.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import BotStyleSelector from "./BotStyleSelector";
import BeiUploadForm from "./components/BeiUploadForm";
import ScreenerUploadForm from "./components/ScreenerUploadForm";
import SyncLastPriceButton from "./components/SyncLastPriceButton";
import SnapshotSignalsButton from "./components/SnapshotSignalsButton";
import BroksumFetchForm from "./components/BroksumFetchForm";
import FetchIndicatorsButton from "./components/FetchIndicatorsButton";
import AdminSpecialMonitoringPanel from "./components/AdminSpecialMonitoringPanel";

import { useAuth } from "./AuthProvider"; 
// import ChatListItem from "./ChatListItem";
// import ChatOptionsMenu from "./ChatOptionsMenu";

const Sidebar = ({
  onSystemMessageChange,
  files,
  selectedIds,       
  onToggleFile,      
  onDeleteFile,
  onUploadFile,
  onViewPdf,
  onViewImage,
  uploading,
  onShowStocksPanel, 
  sidebarWidth = "18rem",
  chatList = [],
  activeChatPublicId,
  onNewChat,      
  onOpenChat,
  onRenameChat,
  onDeleteChat,
  onSelectChat,
  onSelectFile, 
  rightPanelMode,
  setRightPanelMode,
  setSidebarOpen,
}) => {
  const [showBotPanel, setShowBotPanel] = useState(false);
  const [showFilesPanel, setShowFilesPanel] = useState(false);
  const [showScraperPanel, setShowScraperPanel] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState(null); 
  const [showChatList, setShowChatList] = useState(true);
  const { userRole } = useAuth();
  const isAdmin = userRole === "admin";
  const [showStockPanel, setShowStockPanel] = useState(false);
  const [showBeiUpload, setShowBeiUpload] = useState(false);
  const [showScreenerUpload, setShowScreenerUpload] = useState(false);
  const [showSpecialMonitoring, setShowSpecialMonitoring] = useState(false);

  // ⭐ GLOBAL SEARCH STATE
  const [globalSearch, setGlobalSearch] = useState("");

  const searchText = globalSearch.trim().toLowerCase();

  // Filter chat berdasarkan search
  const filteredChatList = useMemo(() => {
    if (!searchText) return chatList || [];
    return (chatList || []).filter((c) =>
      (c.title || "").toLowerCase().includes(searchText)
    );
  }, [chatList, searchText]);

  // Filter files berdasarkan search
  const filteredFiles = useMemo(() => {
    if (!searchText) return files || [];
    return (files || []).filter((f) =>
      (f.filename || "").toLowerCase().includes(searchText)
    );
  }, [files, searchText]);
 
  // helper heading with caret
  const SectionHeader = ({ title, open, onToggle, right }) => (
    <button
      onClick={onToggle}
      className="w-full text-left font-semibold text-gray-200 flex justify-between items-center mb-2"
    >
      <span className="flex items-center gap-2">
        <span className={`transition-transform ${open ? "rotate-90" : ""}`}>▶</span>
        {title}
      </span>
      {right}
    </button>
  );

  useEffect(() => {
    const handleClickOutside = () => setMenuOpenId(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <div
      className="h-full bg-gray-900 border-r border-gray-800 text-gray-300 text-sm pt-16 px-4 overflow-y-auto"
      style={{ width: sidebarWidth }}
    >

      {/* ⭐ GLOBAL SEARCH (CHAT + FILES) */}
      <div className="mb-3 px-2">
        <div className="relative">
          <input
            type="text"
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
            placeholder="Search chats & documents..."
            className="w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-600 text-sm
                       placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          <span className="absolute right-3 top-2.5 text-gray-400 text-xs">🔍</span>
        </div>
      </div>


      {/* ⭐ Stocks Section - Terbagi menjadi User & Admin access */}
      <div className="mb-4">
        <SectionHeader
          title="📈 Stocks"
          open={showScraperPanel}
          onToggle={() => setShowScraperPanel(!showScraperPanel)}
        />
      
        {showScraperPanel && (
          <div className="space-y-2 mt-2">
            
            {/* 1a. AKZES UMUM (All Users) - Tombol Show Panel Stocks */}
            <button
              onClick={() =>
                setRightPanelMode((m) => {
                  const next = m === "stocks" ? null : "stocks";
                  if (next !== null) setSidebarOpen(true);
                  return next;
                })
              }
              className="w-full text-center bg-indigo-600 hover:bg-indigo-500 py-2 px-3 rounded-lg cursor-pointer font-medium text-sm text-white transition"
            >
              <div className="flex items-center justify-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <rect x="3" y="4" width="18" height="16" rx="2" ry="2" strokeWidth="2" />
                  <path d="M3 10h18M9 4v16M15 4v16" strokeWidth="2" />
                </svg>
                Show Panel Stocks
              </div>
            </button>

            {/* 1b. AKZES UMUM (All Users) - Tombol Portfolio */}
            <button
              onClick={() =>
                setRightPanelMode((m) => {
                  const next = m === "portfolio" ? null : "portfolio";
                  if (next !== null) setSidebarOpen(true);
                  return next;
                })
              }
              className="w-full text-center bg-emerald-600 hover:bg-emerald-500 py-2 px-3 rounded-lg cursor-pointer font-medium text-sm text-white transition"
            >
              <div className="flex items-center justify-center gap-2">
                📊 Portfolio
              </div>
            </button>          

            {/* 1c. AKZES UMUM (All Users) - Tombol Watchlist */}
            <button
              onClick={() =>
                setRightPanelMode((m) => {
                  const next = m === "watchlist" ? null : "watchlist";
                  if (next !== null) setSidebarOpen(true);
                  return next;
                })
              }
              className="w-full text-center bg-emerald-600 hover:bg-emerald-500 py-2 px-3 rounded-lg cursor-pointer font-medium text-sm text-white transition"
            >
              <div className="flex items-center justify-center gap-2">
                📊 Watchlist
              </div>
            </button>               
      
            {/* 2. AKSES ADMIN ONLY - Fitur CRUD dan Accuracy */}
            {isAdmin && (
              <>
                {/* Show Accuracy Panel */}
                <button
                  onClick={() =>
                    setRightPanelMode((m) => {
                      const next = m === "accuracy" ? null : "accuracy";
                      if (next !== null) setSidebarOpen(true);
                      return next;
                    })
                  }
                  className="w-full text-center bg-indigo-600 hover:bg-indigo-500 py-2 px-3 rounded-lg cursor-pointer font-medium text-sm text-white transition"
                >
                  <div className="flex items-center justify-center gap-2">
                    📊 Accuracy Panel
                  </div>
                </button>
      
                {/* Upload Data Stock BEI */}
                <button
                  className="w-full py-2 px-3 rounded-lg bg-gray-700 hover:bg-gray-600 text-sm font-semibold text-white text-left"
                  onClick={() => setShowBeiUpload(true)}
                >
                  ⬆️ Upload Data Stock BEI (.xlsx)
                </button>
      
                <SyncLastPriceButton />
      
                <button
                  className="w-full py-2 px-3 rounded-lg bg-gray-700 hover:bg-gray-600 text-sm font-semibold text-white text-left"
                  onClick={() => setShowScreenerUpload(true)}
                >
                  📊 Upload Stock Screener
                </button>
      
                <SnapshotSignalsButton />
                <BroksumFetchForm />
                <FetchIndicatorsButton />
      
                {/* Modal-modal Admin tetap di dalam isAdmin block */}
                {showBeiUpload && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
                    <div className="bg-gray-900 border border-gray-700 rounded-xl p-4 w-full max-w-xl">
                      <div className="flex items-center justify-between mb-3">
                        <h2 className="text-base font-semibold text-white">Upload Ringkasan Saham BEI</h2>
                        <button className="text-gray-300 hover:text-white text-sm" onClick={() => setShowBeiUpload(false)}>✕</button>
                      </div>
                      <BeiUploadForm />
                    </div>
                  </div>
                )}
      
                {showScreenerUpload && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
                    <div className="bg-gray-900 border border-gray-700 rounded-xl p-4 w-full max-w-xl">
                      <div className="flex items-center justify-between mb-3">
                        <h2 className="text-base font-semibold text-white">Upload IDX Stock Screener</h2>
                        <button className="text-gray-300 hover:text-white text-sm" onClick={() => setShowScreenerUpload(false)}>✕</button>
                      </div>
                      <ScreenerUploadForm />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
      
    
      {/* ===== Sidebar Section: Chats ===== */}
      <div className="mb-4">
        <SectionHeader
          title="📝 Chat Lists"
          open={showChatList}
          onToggle={() => setShowChatList(!showChatList)}
        />
    
        {showChatList && (
          <div className="px-2 mt-2">
            <button
              onClick={onNewChat}
              className="w-full text-center bg-indigo-600 hover:bg-indigo-500 py-2 px-3 rounded-lg cursor-pointer font-medium text-sm"
            >
              <div className="flex items-center justify-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                New Chat
              </div>
            </button>
          </div>
        )}
    
        {showChatList && (
          <ul className="mt-2 list-none space-y-1 px-2 text-sm">
            {filteredChatList.length === 0 ? (
              <li className="text-xs text-gray-500 italic">
                {chatList && chatList.length > 0 ? "No chats match your search." : "No chats yet."}
              </li>
            ) : (
              filteredChatList.map((c) => {
                const pid = String(c.public_id || "").trim();     // ✅ harus UUID
                if (!pid) {
                  // kalau masih ada item tanpa public_id berarti backend list_chats belum benar / masih cache lama
                  console.warn("Chat item missing public_id:", c);
                  return null;
                }
              
                const isActive = String(activeChatPublicId || "") === pid;
              
                return (
                  <li
                    key={pid}
                    className={`bg-gray-800 rounded p-2 text-white ${
                      isActive ? "ring-1 ring-indigo-500" : "hover:bg-gray-700"
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <button
                        onClick={() => {
                          onOpenChat(pid);          // ✅ selalu public_id
                          setMenuOpenId(null);
                        }}
                        className="truncate text-left"
                      >
                        {c.title}
                      </button>
              
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpenId(menuOpenId === pid ? null : pid);   // ✅ pid
                        }}
                        className="text-gray-400 hover:text-white px-2"
                      >
                        •••
                      </button>
                    </div>
              
                    {menuOpenId === pid && (
                      <div className="mt-2 pl-2 flex gap-4 text-sm text-blue-300">
                        <button
                          onClick={() => {
                            onRenameChat(pid);      // ✅ public_id
                            setMenuOpenId(null);
                          }}
                          className="hover:underline"
                        >
                          ✏️ Rename
                        </button>
              
                        <button
                          onClick={() => {
                            onDeleteChat(pid);      // ✅ public_id
                            setMenuOpenId(null);
                          }}
                          className="hover:underline text-red-400"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        )}

      </div>
    
      {/* Uploaded Files */}
      <div className="mb-4">
        <SectionHeader
          title="📁 Uploaded Files"
          open={showFilesPanel}
          onToggle={() => setShowFilesPanel(!showFilesPanel)}
        />
    
        {showFilesPanel && (
          <div className="space-y-2">
            {/* Upload Button */}
            <label className="block text-center bg-indigo-600 hover:bg-indigo-500 py-2 px-3 rounded-lg cursor-pointer font-medium text-sm mt-2">
              <div className="flex items-center justify-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Files
              </div>
    
              <input
                type="file"
                multiple
                accept="
                  application/pdf,
                  application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                  application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,
                  application/vnd.openxmlformats-officedocument.presentationml.presentation,
                  image/png,
                  image/jpeg,
                  image/webp"
                onChange={onUploadFile}
                className="hidden"
                disabled={uploading}
              />
            </label>
    
            {uploading && (
              <p className="text-yellow-400 text-xs mt-2 animate-pulse text-center">
                Uploading file...
              </p>
            )}
    
            {(!filteredFiles || filteredFiles.length === 0) ? (
              <p className="text-gray-500 italic text-xs">
                {files && files.length > 0
                  ? "No files match your search."
                  : "No files uploaded."}
              </p>
            ) : (
              <ul className="space-y-1 text-sm list-none">
                {filteredFiles.map((file) => {
                  const id = file.id;
                  const checked = selectedIds?.has?.(id) ?? false;
                  return (
                    <li
                      key={id}
                      className="flex items-center justify-between bg-gray-800 px-2 py-1 rounded hover:bg-gray-700 transition"
                    >
                      <div className="flex items-center gap-2 truncate">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => onToggleFile(id)}
                          className="form-checkbox text-blue-500 rounded bg-gray-900 border-gray-500"
                        />
                        <span className="truncate">{file.filename}</span>
                      </div>
                      <div className="flex gap-2 text-xs">
                        <button
                          onClick={() => {
                            if (file.file_type === "pdf" && file.stored_filename)
                              onViewPdf(file.stored_filename);
                            else if (file.file_type === "image" && file.url)
                              onViewImage(file.url);
                            else console.warn("Cannot view file:", file);
                          }}
                          disabled={
                            (file.file_type === "pdf" && !file.stored_filename) ||
                            (file.file_type === "image" && !file.url)
                          }
                          className="text-blue-400 hover:underline disabled:opacity-40"
                        >
                          View
                        </button>
                        <button
                          onClick={() => onDeleteFile(file.filename)}
                          className="text-red-400 hover:underline"
                        >
                          Delete
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
      
      
      {/* Bot Personality */}
      <div className="mb-6">
        <SectionHeader
          title="🛡️ Bot Personality"
          open={showBotPanel}
          onToggle={() => setShowBotPanel(!showBotPanel)}
        />
        {showBotPanel && (
          <div className="animate-fade">
            <BotStyleSelector onSystemRoleChange={onSystemMessageChange} />
          </div>
        )}
      </div>

      {/* Pemantauan Khusus (BEI) */}
      {isAdmin && (
        <div className="mb-4">
          <SectionHeader
            title="⚠ Pemantauan Khusus (BEI)"
            open={showSpecialMonitoring}
            onToggle={() => setShowSpecialMonitoring(!showSpecialMonitoring)}
          />
      
          {showSpecialMonitoring && (
            <div className="mt-2">
              <AdminSpecialMonitoringPanel />
            </div>
          )}
        </div>
      )}
      
    </div>
  );
};

export default Sidebar;
